import { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { getDurationUnit } from 'utils/durationHelper';
import { Offer, TColumnData, TField } from '../SummaryCard.helpers';
import { TInitialCommission } from './InitialCommission/InitialCommission';

type TGetClipboardTextParams = {
  isUK: boolean;
  withdrawalFee: number;
  offer: TColumnData;
  formattedMonthlyRepayments: string | null;
  formattedRCFWithdrawalFee: string | null;
  formattedFirstMonthlyPayment: string | null;
  formatCurrency: any;
  formatPercentage: any;
  initialCommission?: TInitialCommission;
  isRequestedAmount: boolean;
  fields: TField[];
};

type TOfferSelectedStatus = {
  offerIsSelected: boolean;
  offerNotSelected: boolean;
};

export type TValueProps = {
  offerSelection: TOfferSelectedStatus;
  children?: ReactNode;
  className?: string;
  value?: string;
} & ({ children: ReactNode } | { value: string });

export function getOfferSelectionStatus(
  offerDecision: Offer,
  selectedOfferId: string,
  offerId?: string,
): TOfferSelectedStatus {
  const offerIsSelected =
    selectedOfferId === offerId && offerDecision !== Offer.REJECT;

  const offerNotSelected = Boolean(
    (selectedOfferId && !offerIsSelected && offerDecision === Offer.AGREE) ||
    offerDecision === Offer.REJECT,
  );

  return {
    offerIsSelected,
    offerNotSelected,
  };
}

export function hasRequiredWithdrawalFeeData({
  offer_id,
  withdrawal_fee_percentage,
}: TColumnData) {
  return Boolean(
    offer_id && (withdrawal_fee_percentage || withdrawal_fee_percentage === 0),
  );
}

/**
 * Generate clipboard text
 */
export function getClipboardText({
  isUK,
  offer,
  withdrawalFee,
  formattedMonthlyRepayments,
  formattedRCFWithdrawalFee,
  formattedFirstMonthlyPayment,
  formatCurrency,
  formatPercentage,
  initialCommission,
  isRequestedAmount,
  fields,
}: TGetClipboardTextParams) {
  const { duration, interest_rates, max_amount } = offer;

  const durationValue = duration
    ? `${duration.amount} ${getDurationUnit(duration.unit)}`
    : '';

  const interestRateCopy = fields.includes('interest_rates')
    ? `\n${t`30 day interest rate`}: ${formatPercentage(interest_rates?.[0]?.rate)}`
    : '';

  const withdrawalFeeCopy = fields.includes('withdrawal_fee_percentage')
    ? `\n${t`Withdrawal fee`}: ${formatPercentage(withdrawalFee)}`
    : '';

  const initialCommissionCopy =
    fields.includes('initial_commission') && initialCommission
      ? `\nInitial commission: ${formatPercentage(
          initialCommission.rate,
        )} (${formatCurrency(initialCommission.amount)})`
      : '';

  const monthlyRepaymentsCopy = fields.includes('monthly_repayments')
    ? `\n${t`Monthly repayments`}: ${formattedMonthlyRepayments}`
    : '';

  const rcfWithdrawalFeeCopy = fields.includes('rcf_withdrawal_fee')
    ? `\n${t`Withdrawal fee`}: ${formattedRCFWithdrawalFee}`
    : '';

  const firstMonthlyPaymentCopy = fields.includes('first_monthly_payment')
    ? `\n${t`First Monthly Payment`}: ${formattedFirstMonthlyPayment}`
    : '';

  const repeatCommissionCopy =
    fields.includes('repeat_commission') && initialCommission
      ? `\nRepeat commission: ${formatPercentage(initialCommission.repeat_rate)}`
      : '';

  const PGCopy =
    offer.personal_guarantee_proportion === 0 && isUK
      ? `\nPersonal guarantor: Not required`
      : '';

  const textToCopy = `${t`Term length`}: ${durationValue}\
        \n${isRequestedAmount ? t`Requested amount` : t`Offered amount`}: ${formatCurrency(max_amount)}\
        ${interestRateCopy}\
        ${withdrawalFeeCopy}\
        ${initialCommissionCopy}\
        ${repeatCommissionCopy}\
        ${monthlyRepaymentsCopy}\
        ${rcfWithdrawalFeeCopy}\
        ${firstMonthlyPaymentCopy}\
        ${PGCopy}`;

  return textToCopy;
}
